import type { CheckStatus, PostureSeverity, TenantPosture } from '@/api/cipp';
import { cn } from '@/lib/utils';

/**
 * Shared pieces for the CIPP Security dashboard and the tenant page's Security panel.
 * Status is never colour alone — every chip carries its label.
 */

export type ChipTone = 'good' | 'info' | 'warn' | 'bad' | 'muted';

const CHIP: Record<ChipTone, string> = {
  good: 'bg-success-soft text-success',
  info: 'bg-info-soft text-info',
  warn: 'bg-warning-soft text-warning',
  bad: 'bg-destructive/10 text-destructive',
  muted: 'bg-muted text-muted-foreground',
};

export function Chip({ tone, children, title, className }: { tone: ChipTone; children: React.ReactNode; title?: string; className?: string }) {
  return (
    <span title={title} className={cn('inline-flex items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold', CHIP[tone], className)}>
      {children}
    </span>
  );
}

/** Secure score bands (Microsoft's own % scale). */
export const scoreTone = (pct: number) => (pct >= 70 ? 'bg-success' : pct >= 50 ? 'bg-warning' : 'bg-destructive');

export function ScoreBar({ pct, title }: { pct: number | null | undefined; title?: string }) {
  if (pct == null) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-2" title={title}>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', scoreTone(pct))} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="w-9 text-right text-xs tabular-nums text-foreground">{Math.round(pct)}%</span>
    </div>
  );
}

export const ENFORCEMENT_TONE: Record<string, ChipTone> = {
  'Conditional access': 'good',
  'Security defaults': 'good',
  'Per-user': 'info',
  Mixed: 'info',
  Partial: 'warn',
  None: 'bad',
};

export function EnforcementChip({ mfa }: { mfa: TenantPosture['mfa'] }) {
  if (!mfa?.enforcement) return <span className="text-xs text-muted-foreground">—</span>;
  const title = mfa.unprotected ? `${mfa.unprotected} of ${mfa.members} licensed users have no policy enforcing MFA` : undefined;
  return <Chip tone={ENFORCEMENT_TONE[mfa.enforcement] ?? 'muted'} title={title}>{mfa.enforcement === 'Conditional access' ? 'CA' : mfa.enforcement}</Chip>;
}

/** Registered ÷ members with known registration; tenants without Entra P1 report nothing. */
export function MfaCoverage({ mfa }: { mfa: TenantPosture['mfa'] }) {
  if (!mfa || mfa.members === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const known = mfa.registered + mfa.notRegistered;
  if (known === 0) {
    return <span className="text-xs text-muted-foreground" title="CIPP has no MFA registration data — the tenant has no Entra ID P1 licence">Unknown · no P1</span>;
  }
  const pct = Math.round((mfa.registered / known) * 100);
  return (
    <span className="text-xs tabular-nums" title={`${mfa.registered} of ${known} licensed users registered${mfa.unknown ? ` (${mfa.unknown} unknown)` : ''}`}>
      <span className={cn('font-medium', pct === 100 ? 'text-foreground' : pct >= 90 ? 'text-warning' : 'text-destructive')}>{pct}%</span>
      <span className="text-muted-foreground"> · {mfa.registered}/{known}</span>
    </span>
  );
}

const CHECK_TONE: Record<CheckStatus, ChipTone> = { pass: 'good', warn: 'warn', fail: 'bad' };
const CHECK_LABEL: Record<CheckStatus, string> = { pass: 'Pass', warn: 'Warn', fail: 'Fail' };

export function CheckChip({ status, title }: { status: CheckStatus | null | undefined; title?: string }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return <Chip tone={CHECK_TONE[status]} title={title}>{CHECK_LABEL[status]}</Chip>;
}

export const DMARC_TONE: Record<string, ChipTone> = { reject: 'good', quarantine: 'info', none: 'warn', missing: 'bad' };
export const DMARC_LABEL: Record<string, string> = { reject: 'Reject', quarantine: 'Quarantine', none: 'p=none', missing: 'Missing' };

export function DmarcChip({ mail }: { mail: TenantPosture['mail'] }) {
  if (!mail?.dmarc) return <span className="text-xs text-muted-foreground">—</span>;
  return <Chip tone={DMARC_TONE[mail.dmarc]} title={`DMARC on ${mail.domain}`}>{DMARC_LABEL[mail.dmarc]}</Chip>;
}

const SEVERITY_TONE: Record<PostureSeverity, ChipTone> = { critical: 'bad', warning: 'warn', info: 'muted' };

export function IssueChips({ issues, max = 3 }: { issues: TenantPosture['issues']; max?: number }) {
  if (!issues.length) return <Chip tone="good">No issues</Chip>;
  const shown = issues.slice(0, max);
  const rest = issues.slice(max);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((i) => <Chip key={i.id} tone={SEVERITY_TONE[i.severity]}>{i.label}</Chip>)}
      {rest.length > 0 && <Chip tone="muted" title={rest.map((i) => i.label).join('\n')}>+{rest.length}</Chip>}
    </div>
  );
}

export const hasSeverity = (p: TenantPosture, s: PostureSeverity) => p.issues.some((i) => i.severity === s);
